/**
 * features/hr/PublicHolidays.tsx
 * ------------------------------------------------------------------
 * Admin CRUD for Botswana public holidays (the `publicHolidays`
 * collection), shown as a tab in the Leave area. Used to set next
 * year's gazette dates, add ad-hoc mid-year holidays, and delete.
 *
 * Editing is gated to HR/admin (canEdit); everyone else sees the list
 * read-only. While the collection is empty the built-in 2026 list is
 * shown as a read-only fallback with a one-click "Load" — adding ad-hoc
 * holidays is only enabled once the list is materialised, so the
 * defaults are never silently orphaned. All changes are audited.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { CalendarDays, Plus, Trash2, Download, Loader2, Database, FileText } from 'lucide-react';
import { SoftCard, StatChip, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { HOLIDAYS_2026, type Holiday } from '@/lib/holidays';
import {
  usePublicHolidays,
  savePublicHoliday,
  deletePublicHoliday,
  importHolidays,
} from '@/features/hr/usePublicHolidays';

function pretty(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PublicHolidays({ canEdit = false }: { canEdit?: boolean }) {
  const { holidays, live, loading } = usePublicHolidays();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };

  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const byYear = useMemo(() => {
    const m = new Map<string, Holiday[]>();
    for (const h of holidays) {
      const y = h.date.slice(0, 4);
      if (!m.has(y)) m.set(y, []);
      (m.get(y) as Holiday[]).push(h);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [holidays]);

  async function add() {
    setErr('');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setErr('Pick a valid date.'); return; }
    if (!name.trim()) { setErr('Enter a holiday name.'); return; }
    setBusy(true);
    try { await savePublicHoliday(date, name, actor); setDate(''); setName(''); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function remove(d: string) {
    setBusy(true);
    try { await deletePublicHoliday(d, actor); } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function loadDefaults() {
    setBusy(true);
    try { await importHolidays(HOLIDAYS_2026, actor); } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading holidays…" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="blue" value={String(holidays.length)} label="Holidays listed" icon={CalendarDays} />
        <StatChip tone="slate" value={live ? 'Database' : 'Built-in'} label="Source" icon={live ? Database : FileText} />
      </div>

      {!live && (
        <SoftCard hover={false}>
          <div className="flex items-start gap-3">
            <FileText size={18} className="mt-0.5 shrink-0 text-amber-500" />
            <div className="flex-1">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                Showing the built-in 2026 list as a fallback. It isn’t saved to the database yet, so it can’t be
                edited and the school can’t add its own dates. Load it once to start managing holidays here.
              </p>
              {canEdit && (
                <button
                  onClick={loadDefaults}
                  disabled={busy}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-[#1b3a6b] hover:bg-[#142c52] disabled:opacity-50"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  Load 2026 public holidays
                </button>
              )}
            </div>
          </div>
        </SoftCard>
      )}

      {canEdit && live && (
        <SoftCard hover={false}>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={glassFieldClass('py-2 text-sm w-44')} />
            </label>
            <label className="block flex-1 min-w-[200px]">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Holiday name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Presidential Inauguration" className={glassFieldClass('py-2 text-sm')} />
            </label>
            <AccentButton icon={busy ? Loader2 : Plus} disabled={busy} onClick={add}>Add holiday</AccentButton>
          </div>
          {err && <p className="text-red-600 dark:text-red-400 text-sm mt-2">{err}</p>}
          <p className="mt-2 text-[11px] text-gray-400">
            Adding the same date again just updates its name. Use this for next year’s gazette dates or ad-hoc closures.
          </p>
        </SoftCard>
      )}

      {byYear.length === 0 ? (
        <SoftCard hover={false}><p className="text-sm text-gray-500 dark:text-gray-400">No holidays on file.</p></SoftCard>
      ) : (
        byYear.map(([year, list]) => (
          <SoftCard key={year} pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{year}</h3>
              <span className="text-xs text-gray-400">{list.length} day{list.length === 1 ? '' : 's'}</span>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {list.map((h) => (
                <div key={h.date} className="flex items-center justify-between px-5 py-2.5">
                  <div className="text-sm">
                    <span className="font-medium text-gray-800 dark:text-gray-100">{pretty(h.date)}</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-300">{h.name}</span>
                  </div>
                  {canEdit && live && (
                    <button
                      onClick={() => remove(h.date)}
                      disabled={busy}
                      title="Delete holiday"
                      className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </SoftCard>
        ))
      )}
    </div>
  );
}
